import { useEffect, useMemo, useState } from 'react';
import ExerciseRenderer from '../exercises/ExerciseRenderer';
import LessonBlockView from '../components/LessonBlockView';
import type { Exercise } from '../exercises/types';
import { readyPlanLabel, selectReadyStageBlocks } from './readyLessonBlocks';
import {
  accessibleLessons, courses, createHomework, createLessonBlock, lessonBlocks, readyLessons, teachingOverview, logout, pairs,
  reviewSubmission, submissions, unlock, type Course, type Pair, type LessonSummary, type LessonBlockData,
  type SubmissionItem, type TeachingOverview,
  reorderLessonBlocks,
} from '../lib/api';

interface Props { onLogout: () => void; }

type StudioType = 'reading' | 'vocabulary' | 'grammar' | 'listening' | 'video' | 'image' | 'speaking' | 'dialogue' | 'writing' | 'exercise' | 'revision' | 'homework';

const exerciseTypeOptions = [
  ['multiple-choice', 'Multiple choice'], ['true-false', 'True / False'], ['fill-gap', 'Fill the gap'],
  ['matching', 'Matching'], ['ordering', 'Ordering / drag-drop'], ['dictation', 'Dictation + audio'],
  ['listening', 'Listening'], ['speaking-answer', 'Speaking / recording'], ['translation', 'Translation'],
  ['short-answer', 'Short answer'], ['message', 'Message'], ['email', 'Email'], ['essay', 'Essay'],
  ['before-watching', 'Before video'], ['while-watching', 'While video'], ['after-watching', 'After video'],
] as const;

const studioTypes: Array<{ value: StudioType; label: string; hint: string }> = [
  { value: 'reading', label: 'Reading', hint: 'Explanation, source text or reading passage.' },
  { value: 'vocabulary', label: 'Vocabulary', hint: 'Words, examples and short practice notes.' },
  { value: 'grammar', label: 'Grammar', hint: 'Rule, examples and a compact explanation.' },
  { value: 'listening', label: 'Listening', hint: 'Listening instructions or transcript until media is connected.' },
  { value: 'video', label: 'Video', hint: 'Video task instructions or a linked resource.' },
  { value: 'image', label: 'Image', hint: 'Visual prompt or teacher-selected picture task.' },
  { value: 'speaking', label: 'Speaking', hint: 'Partner/teacher speaking prompt.' },
  { value: 'dialogue', label: 'Dialogue', hint: 'A short conversation students can read and perform.' },
  { value: 'writing', label: 'Writing', hint: 'Message, email, paragraph or other writing task.' },
  { value: 'exercise', label: 'Exercise', hint: 'Interactive multiple-choice, true/false or text exercise.' },
  { value: 'revision', label: 'Revision', hint: 'Recall prompt for the revision layer.' },
  { value: 'homework', label: 'Homework', hint: 'A compact task list to complete after the lesson.' },
];

function parseBlock(block: LessonBlockData): Record<string, unknown> {
  try { return JSON.parse(block.payload_json) as Record<string, unknown>; } catch { return { text: block.payload_json }; }
}

function blockPreview(block: LessonBlockData) {
  const payload = parseBlock(block);
  const text = typeof payload.text === 'string' ? payload.text : '';
  if (block.block_type === 'exercise' && payload.exercise && typeof payload.exercise === 'object') {
    const exercise = payload.exercise as Record<string, unknown>;
    return String(exercise.prompt || 'Interactive exercise');
  }
  if (block.block_type === 'dialogue' && Array.isArray(payload.lines)) return (payload.lines as Array<Record<string, unknown>>).map((line) => String(line.text || '')).join(' · ');
  if (block.block_type === 'homework' && Array.isArray(payload.tasks)) return (payload.tasks as unknown[]).map(String).join(' · ');
  return text || String(payload.prompt || 'Structured lesson block');
}

export default function TeacherDashboard({ onLogout }: Props) {
  const [courseList, setCourseList] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [pairList, setPairList] = useState<Pair[]>([]);
  const [lessonList, setLessonList] = useState<(LessonSummary & { unlocked: number })[]>([]);
  const [selectedLesson, setSelectedLesson] = useState('');
  const [message, setMessage] = useState('');
  const [homeworkPair, setHomeworkPair] = useState('');
  const [homeworkStudent, setHomeworkStudent] = useState('');
  const [homeworkDue, setHomeworkDue] = useState('');
  const [homeworkTitle, setHomeworkTitle] = useState('10-minute practice');
  const [homeworkInstructions, setHomeworkInstructions] = useState('');
  const [homeworkKind, setHomeworkKind] = useState('writing');
  const [submissionList, setSubmissionList] = useState<SubmissionItem[]>([]);
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [overview, setOverview] = useState<TeachingOverview | null>(null);
  const [selectedStudent, setSelectedStudent] = useState('');

  const [studioBlocks, setStudioBlocks] = useState<LessonBlockData[]>([]);
  const [studioType, setStudioType] = useState<StudioType>('reading');
  const [studioTitle, setStudioTitle] = useState('');
  const [studioText, setStudioText] = useState('');
  const [studioPrompt, setStudioPrompt] = useState('');
  const [studioOptions, setStudioOptions] = useState('');
  const [studioAnswer, setStudioAnswer] = useState('');
  const [studioExerciseKind, setStudioExerciseKind] = useState('multiple-choice');
  const [studioMediaUrl, setStudioMediaUrl] = useState('');
  const [studioImageUrl, setStudioImageUrl] = useState('');
  const [studioTeacherNote, setStudioTeacherNote] = useState('');
  const [studioLines, setStudioLines] = useState('Teacher: Guten Tag!\nStudent: Guten Tag!\nTeacher: Wie heißt du?');
  const [studioTasks, setStudioTasks] = useState('Read the examples aloud.\nWrite three answers.\nSay your answers to your partner.');
  const [studioBusy, setStudioBusy] = useState(false);
  const [studioPreview, setStudioPreview] = useState(false);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [studioLessonSearch, setStudioLessonSearch] = useState('');
  const [studioUnitFilter, setStudioUnitFilter] = useState<number | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'ready' | 'studio' | 'teaching'>('ready');
  const [readyRun, setReadyRun] = useState(false);
  const levelOrder: Record<string, number> = { a0: 0, a1: 1, a2: 2, b1: 3, b2: 4, c1: 5 };

  const [readyRows, setReadyRows] = useState<Awaited<ReturnType<typeof readyLessons>>>([]);
  const [selectedReadyPlan, setSelectedReadyPlan] = useState('');
  const [readySourceBlocks, setReadySourceBlocks] = useState<LessonBlockData[]>([]);

  useEffect(() => {
    Promise.all([courses(), pairs()]).then(([c, p]) => {
      const orderedCourses = [...c].sort((a, b) => (a.language === b.language ? (levelOrder[a.level.toLowerCase()] ?? 99) - (levelOrder[b.level.toLowerCase()] ?? 99) : a.language.localeCompare(b.language)));
      setCourseList(orderedCourses);
      setPairList(p);
      if (c[0]) setSelectedCourse(c[0].id);
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load teaching workspace.'));
  }, []);

  useEffect(() => {
    if (!homeworkPair) { setOverview(null); setSubmissionList([]); return; }
    teachingOverview(homeworkPair).then((data) => {
      setOverview(data);
      setSelectedStudent((current) => current && data.students.some((student) => student.id === current) ? current : data.students[0]?.id || '');
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load teaching overview.'));
  }, [homeworkPair]);

  useEffect(() => {
    if (!homeworkPair) { setSubmissionList([]); return; }
    submissions(homeworkPair).then(setSubmissionList).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load submissions.'));
  }, [homeworkPair]);

  useEffect(() => {
    if (!selectedCourse) return;
    readyLessons(selectedCourse).then((rows) => {
      setReadyRows(rows);
      if (rows[0]) setSelectedReadyPlan((current) => current && rows.some((row) => row.plan_id === current) ? current : rows[0].plan_id);
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load ready lessons.'));
    accessibleLessons(selectedCourse).then((l) => {
      setLessonList(l);
      if (l[0]) setSelectedLesson((current) => current && l.some((item) => item.id === current) ? current : l[0].id);
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load lessons.'));
  }, [selectedCourse]);

  useEffect(() => {
    if (!selectedLesson) { setStudioBlocks([]); return; }
    lessonBlocks(selectedLesson).then(setStudioBlocks).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load lesson studio.'));
  }, [selectedLesson]);

  async function openLesson(pair: Pair, studentId?: string | null) {
    if (!selectedLesson) return;
    try {
      await unlock(selectedLesson, pair.id, studentId);
      setMessage(studentId ? 'Lesson unlocked for the selected student.' : 'Lesson unlocked for the whole pair.');
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not unlock lesson.'); }
  }

  const filteredStudioLessons = useMemo(() => {
    const q = studioLessonSearch.trim().toLowerCase();
    return lessonList.filter((lesson) => {
      const unitMatch = studioUnitFilter === 'all' || lesson.unit_number === studioUnitFilter;
      return unitMatch && (!q || lesson.title.toLowerCase().includes(q));
    });
  }, [lessonList, studioLessonSearch, studioUnitFilter]);

  const readyPlans = useMemo(() => {
    const map = new Map<string, typeof readyRows>();
    for (const row of readyRows) {
      const current = map.get(row.plan_id) || [];
      current.push(row);
      map.set(row.plan_id, current);
    }
    return Array.from(map.values()).sort((a, b) => (a[0]?.sequence_rank || 0) - (b[0]?.sequence_rank || 0));
  }, [readyRows]);

  const selectedReadyRows = readyPlans.find((rows) => rows[0]?.plan_id === selectedReadyPlan) || [];

  useEffect(() => {
    const lessonId = selectedReadyRows[0]?.lesson_id;
    if (!lessonId || !readyRun) { setReadySourceBlocks([]); return; }
    lessonBlocks(lessonId).then(setReadySourceBlocks).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load ready lesson materials.'));
  }, [selectedReadyPlan, readyRun]);

  const readyStageBlocks = useMemo(
    () => selectReadyStageBlocks(selectedReadyRows, readySourceBlocks),
    [selectedReadyRows, readySourceBlocks],
  );

  const selectedStudioMeta = studioTypes.find((item) => item.value === studioType)!;

  function applyStudioTemplate(template: 'listening' | 'writing' | 'video' | 'speaking' | 'matching' | 'ordering' | 'dictation') {
    setStudioPreview(false);
    if (template === 'listening') {
      setStudioType('exercise'); setStudioExerciseKind('listening'); setStudioTitle('Listen & choose'); setStudioText(''); setStudioMediaUrl(''); setStudioPrompt('What does the speaker say first?'); setStudioOptions('Guten Tag\nHallo\nGute Nacht'); setStudioAnswer('Hallo'); setStudioTeacherNote('Replay once more and listen for the greeting.');
    } else if (template === 'writing') {
      setStudioType('exercise'); setStudioExerciseKind('email'); setStudioTitle('Write a short email'); setStudioPrompt('Write a short email to your teacher. Say hello, introduce yourself and ask one question.'); setStudioText(''); setStudioOptions(''); setStudioAnswer(''); setStudioMediaUrl(''); setStudioTeacherNote('Aim for 40–60 words. Check greeting, word order and punctuation.');
    } else if (template === 'video') {
      setStudioType('exercise'); setStudioExerciseKind('after-watching'); setStudioTitle('After watching'); setStudioPrompt('Watch the video and write one sentence about the main idea.'); setStudioMediaUrl(''); setStudioOptions(''); setStudioAnswer(''); setStudioTeacherNote('Pause and replay any part you did not understand.');
    } else if (template === 'dictation') {
      setStudioType('exercise'); setStudioExerciseKind('dictation'); setStudioTitle('Listen & write'); setStudioPrompt('Listen to the word and type exactly what you hear.'); setStudioMediaUrl(''); setStudioOptions(''); setStudioAnswer(''); setStudioTeacherNote('Replay the audio, then check spelling and umlauts.');
    } else if (template === 'matching') {
      setStudioType('exercise'); setStudioExerciseKind('matching'); setStudioTitle('Match the words'); setStudioPrompt('Match each German word with its meaning.'); setStudioOptions('Wasser = water\nBuch = book\nSonne = sun'); setStudioAnswer(''); setStudioMediaUrl(''); setStudioTeacherNote('Read both sides before checking.');
    } else if (template === 'ordering') {
      setStudioType('exercise'); setStudioExerciseKind('ordering'); setStudioTitle('Build the sentence'); setStudioPrompt('Put the words in the correct order.'); setStudioOptions('heiße\nich\nAnna'); setStudioAnswer('ich,heiße,Anna'); setStudioMediaUrl(''); setStudioTeacherNote('Remember: Ich heiße Anna.');
    } else {
      setStudioType('exercise'); setStudioExerciseKind('speaking-answer'); setStudioTitle('Speak about yourself'); setStudioPrompt('Introduce yourself: name, where you are from and one thing you like.'); setStudioOptions(''); setStudioAnswer(''); setStudioMediaUrl(''); setStudioTeacherNote('Speak for 30–45 seconds. Focus on clear pronunciation.');
    }
  }

  const buildPayload = (): Record<string, unknown> => {
    if (studioType === 'exercise') {
      const normalizedOptions = studioOptions.split('\n').map((x) => x.trim()).filter(Boolean);
      const normalizedAnswers = studioAnswer.split(',').map((x) => x.trim()).filter(Boolean);
      return {
        exercise: {
          id: crypto.randomUUID(),
          type: studioExerciseKind,
          title: studioTitle.trim(),
          prompt: studioPrompt.trim(),
          options: normalizedOptions,
          answer: normalizedAnswers,
          difficulty: 'A0',
          audioUrl: ['listening','dictation'].includes(studioExerciseKind) ? studioMediaUrl.trim() || undefined : undefined,
          videoUrl: ['before-watching','while-watching','after-watching'].includes(studioExerciseKind) ? studioMediaUrl.trim() || undefined : undefined,
          teacherNote: studioTeacherNote.trim() || undefined,
          imageUrl: ['picture-description','image-word','word-image'].includes(studioExerciseKind) ? studioImageUrl.trim() || undefined : undefined,
        },
      };
    }
    if (studioType === 'dialogue') {
      const lines = studioLines.split('\n').map((line) => {
        const separator = line.indexOf(':');
        return separator > -1
          ? { speaker: line.slice(0, separator).trim(), text: line.slice(separator + 1).trim() }
          : { speaker: 'Speaker', text: line.trim() };
      }).filter((line) => line.text);
      return { lines };
    }
    if (studioType === 'homework') {
      return { tasks: studioTasks.split('\n').map((x) => x.trim()).filter(Boolean) };
    }
    if (studioType === 'speaking' || studioType === 'revision') {
      return { prompt: studioPrompt.trim(), teacherNote: studioTeacherNote.trim() || undefined };
    }
    if (studioType === 'image') {
      return { prompt: studioPrompt.trim(), image_url: studioImageUrl.trim() || undefined, teacherNote: studioTeacherNote.trim() || undefined };
    }
    if (studioType === 'listening') {
      return { text: studioText.trim(), audio_url: studioMediaUrl.trim() || undefined };
    }
    if (studioType === 'video') {
      return { text: studioText.trim(), video_url: studioMediaUrl.trim() || undefined };
    }
    return { text: studioText.trim() };
  };

  const studioValid = !!selectedLesson && !!studioTitle.trim() && (
    studioType === 'exercise' ? !!studioPrompt.trim() :
    studioType === 'dialogue' ? studioLines.split('\n').some((line) => line.includes(':')) :
    studioType === 'homework' ? studioTasks.split('\n').some((x) => x.trim()) :
    ['speaking','revision','image'].includes(studioType) ? !!studioPrompt.trim() :
    !!studioText.trim()
  );

  async function saveStudioBlock() {
    if (!studioValid) return;
    setStudioBusy(true);
    try {
      const payload = buildPayload();
      await createLessonBlock({
        lesson_id: selectedLesson,
        title: studioTitle.trim(),
        block_type: studioType,
        text: typeof payload.text === 'string' ? payload.text : '',
        payload,
      });
      const refreshed = await lessonBlocks(selectedLesson);
      setStudioBlocks(refreshed);
      setMessage('Material saved to the lesson. It is now part of the same content pipeline as the source material.');
      setStudioTitle('');
      setStudioText('');
      setStudioPrompt('');
      setStudioOptions('');
      setStudioAnswer('');
      setStudioExerciseKind('multiple-choice');
      setStudioMediaUrl('');
      setStudioImageUrl('');
      setStudioTeacherNote('');
      setStudioPreview(false);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not save material.');
    } finally {
      setStudioBusy(false);
    }
  }

  const selectedLessonInfo = lessonList.find((lesson) => lesson.id === selectedLesson);
  const previewExercise = useMemo<Exercise | null>(() => {
    if (studioType !== 'exercise' || !studioPrompt.trim()) return null;
    const options = studioOptions.split('\n').map((x) => x.trim()).filter(Boolean);
    const answer = studioAnswer.split(',').map((x) => x.trim()).filter(Boolean);
    const pairs = options.map((item) => {
      const separator = item.includes('=') ? '=' : '|';
      const [left, ...right] = item.split(separator);
      return { left: left.trim(), right: right.join(separator).trim() };
    }).filter((pair) => pair.left && pair.right);
    return {
      id: 'studio-preview',
      type: studioExerciseKind as Exercise['type'],
      title: studioTitle.trim() || 'Preview exercise',
      prompt: studioPrompt.trim(),
      source: 'talkup-original',
      difficulty: 'A0',
      options: studioExerciseKind === 'matching' ? pairs.map((pair) => pair.left + ' = ' + pair.right) : options,
      answer: studioExerciseKind === 'ordering' ? answer : answer.length === 1 ? answer[0] : answer,
      pairs: studioExerciseKind === 'matching' ? pairs : undefined,
      audioUrl: ['listening','dictation'].includes(studioExerciseKind) ? studioMediaUrl.trim() || undefined : undefined,
      videoUrl: ['before-watching','while-watching','after-watching'].includes(studioExerciseKind) ? studioMediaUrl.trim() || undefined : undefined,
      imageUrl: studioImageUrl.trim() || undefined,
      teacherNote: studioTeacherNote.trim() || undefined,
    };
  }, [studioType, studioPrompt, studioOptions, studioAnswer, studioExerciseKind, studioTitle, studioMediaUrl, studioImageUrl, studioTeacherNote]);
  const groupedStudio = useMemo(() => studioBlocks.map((block) => ({
    block,
    preview: blockPreview(block),
    stage: ({
      vocabulary: '1 · Learn',
      grammar: '1 · Learn',
      reading: '1 · Learn',
      dialogue: '2 · Practice',
      exercise: '2 · Practice',
      listening: '3 · Listen',
      video: '3 · Watch',
      image: '2 · Practice',
      speaking: '4 · Speak',
      writing: '5 · Write',
      revision: '6 · Review',
      homework: '7 · Homework',
    } as Record<string, string>)[block.block_type] || '2 · Practice',
  })), [studioBlocks]);

  async function moveStudioBlock(index: number, direction: -1 | 1) {
    if (!selectedLesson || reorderBusy) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= studioBlocks.length) return;
    const next = [...studioBlocks];
    const [moved] = next.splice(index, 1);
    next.splice(nextIndex, 0, moved);
    setReorderBusy(true);
    setStudioBlocks(next.map((block, order) => ({ ...block, block_order: order + 1 })));
    try {
      await reorderLessonBlocks(selectedLesson, next.map((block) => block.id));
      setMessage('Lesson flow updated. Students will see the new order.');
    } catch (e) {
      const refreshed = await lessonBlocks(selectedLesson);
      setStudioBlocks(refreshed);
      setMessage(e instanceof Error ? e.message : 'Could not reorder lesson blocks.');
    } finally {
      setReorderBusy(false);
    }
  }

  return <section className="teacher-page">
    <header className="teacher-header">
      <div>
        <p className="eyebrow">TALKUP · TEACHER</p>
        <h1>Your teaching room</h1>
        <p>One workspace for lesson access, Content Studio, homework and the Learning Pulse. Existing source material stays intact; your additions sit on top.</p>
      </div>
      <button className="back" onClick={async () => { await logout(); onLogout(); }}>Sign out</button>
    </header>

    {message && <div className="panel" role="status">{message}</div>}
    <nav className="teacher-workspace-tabs" aria-label="Teacher workspace">
      <button type="button" className={activeTab === 'ready' ? 'active' : ''} onClick={() => { setActiveTab('ready'); setReadyRun(false); }}>Ready 60-min lessons</button>
      <button type="button" className={activeTab === 'studio' ? 'active' : ''} onClick={() => { setActiveTab('studio'); setReadyRun(false); }}>Content Studio</button>
      <button type="button" className={activeTab === 'teaching' ? 'active' : ''} onClick={() => setActiveTab('teaching')}>Teaching & homework</button>
    </nav>

    {activeTab === 'ready' && <section className="panel ready-lessons-panel">
      <div className="ready-lessons-head">
        <div>
          <p className="eyebrow">TALKUP READY LESSON LIBRARY</p>
          <h2>Готовые уроки · 60 минут</h2>
          <p>Уроки уже собраны и сохранены в программе. Они идут последовательно от A0 до C1 и не генерируются случайно.</p>
        </div>
        <span className="ready-duration">60 MIN</span>
      </div>
      <label className="material-label">Курс
        <select value={selectedCourse} onChange={(e) => { setSelectedCourse(e.target.value); setReadyRun(false); }}>
          {courseList.map((course) => <option key={course.id} value={course.id}>{course.language === 'de' ? 'Deutsch' : 'English'} · {course.level.toUpperCase()} · {course.title}</option>)}
        </select>
      </label>
      <div className="ready-curriculum">
        {readyPlans.map((rows) => {
          const first = rows[0];
          const planLabel = readyPlanLabel(first.profile);
          return <article className={selectedReadyPlan === first.plan_id ? 'ready-lesson-card selected' : 'ready-lesson-card'} key={first.plan_id}>
            <div>
              <span className="eyebrow">UNIT {first.unit_number} · LESSON {first.lesson_number} · {first.level.toUpperCase()}</span>
              {planLabel && <span className="eyebrow">{planLabel}</span>}
              <h3>{first.lesson_title}</h3>
              <p>{first.lesson_description || 'Готовый урок на 60 минут · 7 последовательных этапов'}</p>
            </div>
            <button type="button" onClick={() => { setSelectedReadyPlan(first.plan_id); setReadyRun(true); }}>Начать урок</button>
          </article>;
        })}
        {!readyPlans.length && <div className="material-empty">Для этого курса пока нет готовых уроков.</div>}
      </div>
      {readyRun && selectedReadyRows.length > 0 && <div className="ready-run">
        <div className="ready-run-head">
          <div><span className="eyebrow">{selectedReadyRows[0].language === 'de' ? 'JETZT · UNTERRICHT' : 'NOW TEACHING'}</span><h3>{selectedReadyRows[0].lesson_title}</h3><p>{selectedReadyRows[0].language === 'de' ? 'Fertiger 60-Minuten-Unterricht mit den Materialien dieser Lektion.' : 'Ready 60-minute lesson using this lesson’s materials.'}</p></div>
          <button type="button" className="back" onClick={() => setReadyRun(false)}>Назад</button>
        </div>
        <div className="ready-timeline">
          {selectedReadyRows.map((row) => {
            const block = readyStageBlocks.get(row.step_order) || null;
            return <article className="ready-stage" key={row.step_order}>
              <div className="ready-stage-time"><b>{row.minutes}</b><span>min</span></div>
              <div className="ready-stage-body">
                <span className="eyebrow">{row.stage_title}</span>
                <p className="ready-teacher-instruction">{row.teacher_instruction}</p>
                {block ? <><h4>{block.title}</h4><LessonBlockView block={block} interactive={false} /></> : <p className="material-empty" role="status">{row.language === 'de' ? 'Für diese Unterrichtsphase ist noch kein passendes Material verknüpft.' : 'No lesson material is linked to this stage yet.'}</p>}
              </div>
            </article>;
          })}
        </div>
      </div>}
    </section>}

    {activeTab === 'studio' && <>
    <section className="panel teacher-lesson-picker">
      <div className="material-toolbar">
        <label className="material-label">Course
          <select value={selectedCourse} onChange={(e) => { setSelectedCourse(e.target.value); setStudioLessonSearch(''); setStudioUnitFilter('all'); }}>
            {courseList.map((course) => <option key={course.id} value={course.id}>{course.language.toUpperCase()} · {course.level} · {course.title}</option>)}
          </select>
        </label>
        <label className="material-label">Find lesson
          <input value={studioLessonSearch} onChange={(e) => setStudioLessonSearch(e.target.value)} placeholder="Search lesson…" />
        </label>
        <label className="material-label">Unit
          <select value={studioUnitFilter} onChange={(e) => setStudioUnitFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">All units</option>
            {[...new Set(lessonList.map((l) => l.unit_number))].sort((a,b)=>a-b).map((unit) => <option key={unit} value={unit}>Unit {unit}</option>)}
          </select>
        </label>
      </div>
      <div className="studio-curriculum-strip">
        {filteredStudioLessons.map((lesson) => <button key={lesson.id} className={selectedLesson === lesson.id ? 'active' : ''} onClick={() => setSelectedLesson(lesson.id)}>
          <span>U{lesson.unit_number} · L{lesson.lesson_number}</span><b>{lesson.title}</b><small>{Number(lesson.block_count || 0)} blocks</small>
        </button>)}
        {!filteredStudioLessons.length && <span className="studio-curriculum-empty">No lessons match this filter.</span>}
      </div>
      {selectedLessonInfo && <div className="studio-context"><span>EDITING</span><strong>Unit {selectedLessonInfo.unit_number} · Lesson {selectedLessonInfo.lesson_number}</strong><b>{selectedLessonInfo.title}</b><small>{Number(selectedLessonInfo.block_count || 0)} curriculum blocks</small></div>}
    </section>

    <section className="panel content-studio">
      <div className="studio-header">
        <div>
          <p className="eyebrow">CONTENT STUDIO</p>
          <h2>Build this lesson from reusable blocks</h2>
          <p>Choose a block type, fill only the fields that matter, preview it, then save. The same <code>content_blocks</code> pipeline is used by the student lesson.</p>
        </div>
        <div className="studio-count"><b>{studioBlocks.length}</b><span>blocks in lesson</span></div>
      </div>

      <div className="studio-workspace">
        <div className="studio-editor">
          <div className="studio-templates">
            <span className="studio-help">QUICK START</span>
            <button type="button" onClick={() => applyStudioTemplate('listening')}>🎧 Listening</button>
            <button type="button" onClick={() => applyStudioTemplate('dictation')}>⌨️ Dictation</button>
            <button type="button" onClick={() => applyStudioTemplate('matching')}>🔗 Matching</button>
            <button type="button" onClick={() => applyStudioTemplate('ordering')}>↕ Ordering</button>
            <button type="button" onClick={() => applyStudioTemplate('writing')}>✍️ Writing</button>
            <button type="button" onClick={() => applyStudioTemplate('video')}>▶ Video</button>
            <button type="button" onClick={() => applyStudioTemplate('speaking')}>🎙 Speaking</button>
          </div>
          <div className="studio-type-grid">
            {studioTypes.map((item) => <button key={item.value} type="button" className={studioType === item.value ? 'active' : ''} onClick={() => setStudioType(item.value)}>
              <b>{item.label}</b><span>{item.hint}</span>
            </button>)}
          </div>

          <div className="studio-form">
            <label className="material-label">Block title
              <input value={studioTitle} onChange={(e) => setStudioTitle(e.target.value)} placeholder="e.g. Introducing yourself" />
            </label>

            {studioType === 'exercise' && <>
              <label className="material-label">Exercise type
                <select value={studioExerciseKind} onChange={(e) => setStudioExerciseKind(e.target.value)}>
                  {exerciseTypeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="material-label">Prompt
                <textarea rows={3} value={studioPrompt} onChange={(e) => setStudioPrompt(e.target.value)} placeholder="Choose the correct German greeting." />
              </label>
              {['listening','dictation','before-watching','while-watching','after-watching'].includes(studioExerciseKind) && <label className="material-label">Media URL <span className="studio-help">audio or video file URL</span>
                <input value={studioMediaUrl} onChange={(e) => setStudioMediaUrl(e.target.value)} placeholder="https://…/lesson-media.mp3 or .mp4" />
              </label>}
              {['picture-description','image-word','word-image'].includes(studioExerciseKind) && <label className="material-label">Image URL <span className="studio-help">optional · direct image URL</span>
                <input value={studioImageUrl} onChange={(e) => setStudioImageUrl(e.target.value)} placeholder="https://…/picture.jpg" />
              </label>}
              <div className="studio-two-col">
                <label className="material-label">Options / items <span className="studio-help">one per line; matching can use left = right</span>
                  <textarea rows={5} value={studioOptions} onChange={(e) => setStudioOptions(e.target.value)} placeholder={'Guten Morgen\nGute Nacht\nDanke'} />
                </label>
                <label className="material-label">Correct answer <span className="studio-help">comma-separated if needed</span>
                  <textarea rows={5} value={studioAnswer} onChange={(e) => setStudioAnswer(e.target.value)} placeholder="Guten Morgen" />
                </label>
              </div>
              <label className="material-label">Teacher note <span className="studio-help">optional</span>
                <input value={studioTeacherNote} onChange={(e) => setStudioTeacherNote(e.target.value)} placeholder="Ask the student to explain why." />
              </label>
            </>}

            {studioType === 'dialogue' && <label className="material-label">Dialogue <span className="studio-help">Speaker: line, one per row</span>
              <textarea rows={7} value={studioLines} onChange={(e) => setStudioLines(e.target.value)} />
            </label>}

            {studioType === 'homework' && <label className="material-label">Task list <span className="studio-help">one task per line · keep it 10–15 minutes</span>
              <textarea rows={7} value={studioTasks} onChange={(e) => setStudioTasks(e.target.value)} />
            </label>}

            {['speaking','revision','image'].includes(studioType) && <>
              <label className="material-label">Prompt
                <textarea rows={4} value={studioPrompt} onChange={(e) => setStudioPrompt(e.target.value)} placeholder={studioType === 'speaking' ? 'Introduce yourself to your partner.' : 'Recall three words without looking.'} />
              </label>
              {studioType === 'image' && <label className="material-label">Image URL <span className="studio-help">optional · direct image URL</span>
                <input value={studioImageUrl} onChange={(e) => setStudioImageUrl(e.target.value)} placeholder="https://…/picture.jpg" />
              </label>}
              <label className="material-label">Teacher note <span className="studio-help">optional</span>
                <input value={studioTeacherNote} onChange={(e) => setStudioTeacherNote(e.target.value)} placeholder="Useful follow-up question or classroom cue." />
              </label>
            </>}

            {['listening','video'].includes(studioType) && <label className="material-label">Media URL <span className="studio-help">direct audio/video file URL</span>
              <input value={studioMediaUrl} onChange={(e) => setStudioMediaUrl(e.target.value)} placeholder={studioType === 'listening' ? 'https://…/audio.mp3' : 'https://…/video.mp4'} />
            </label>}

            {!['exercise','dialogue','homework','speaking','revision','image'].includes(studioType) && <label className="material-label">Content
              <textarea rows={9} value={studioText} onChange={(e) => setStudioText(e.target.value)} placeholder={selectedStudioMeta.hint} />
            </label>}

            <div className="studio-actions">
              <button className="secondary-action" type="button" onClick={() => setStudioPreview((value) => !value)}>{studioPreview ? 'Hide preview' : 'Preview block'}</button>
              <button type="button" disabled={!studioValid || studioBusy} onClick={saveStudioBlock}>{studioBusy ? 'Saving…' : 'Save block to lesson'}</button>
            </div>
          </div>

          {studioPreview && <article className="studio-live-preview">
            <div className="studio-preview-head"><div><span className="eyebrow">LIVE PREVIEW</span><h3>{studioTitle || 'Untitled block'}</h3></div><span>Student view</span></div>
            {studioType === 'exercise' && previewExercise ? <div className="studio-exercise-preview"><ExerciseRenderer exercise={previewExercise} /></div> :
             studioType === 'dialogue' ? <div className="dialogue-list">{studioLines.split('\n').filter(Boolean).map((line, i) => { const [speaker, ...rest] = line.split(':'); return <div className={i % 2 ? 'dialogue-bubble alt' : 'dialogue-bubble'} key={line + i}><span>{speaker}</span><strong>{rest.join(':').trim()}</strong></div>; })}</div> :
             studioType === 'homework' ? <div className="task-list">{studioTasks.split('\n').filter(Boolean).map((task, i) => <div key={task + i}><b>{String(i + 1).padStart(2,'0')}</b><span>{task}</span></div>)}</div> :
             studioType === 'image' && studioImageUrl ? <div className="studio-image-preview"><img src={studioImageUrl} alt="Lesson preview" /><p>{studioPrompt}</p></div> :
             <div className="studio-document-preview"><p>{studioText || studioPrompt || 'Start writing to see the block preview.'}</p>{studioTeacherNote && <small>{studioTeacherNote}</small>}</div>}
          </article>}
        </div>

        <aside className="studio-existing">
          <div className="studio-existing-head"><div><span className="eyebrow">LESSON FLOW</span><h3>Current blocks</h3></div><small>{studioBlocks.length} total</small></div>
          {!groupedStudio.length ? <div className="material-empty">No blocks yet. Add the first TalkUp Original above.</div> :
            <>
            <div className="studio-flow-steps">
              <span>1 Learn</span><span>2 Practice</span><span>3 Listen / Watch</span><span>4 Speak</span><span>5 Write</span><span>6 Review</span><span>7 Homework</span>
            </div>
            <div className="studio-block-list">{groupedStudio.map(({ block, preview, stage }, index) => <article key={block.id} className="studio-flow-block">
              <div className="studio-flow-main">
                <span className="studio-order">{String(index + 1).padStart(2,'0')}</span>
                <div><b>{block.title}</b><small>{stage} · {block.block_type.replace(/_/g, ' ')} · {block.source}</small></div>
              </div>
              <p>{preview}</p>
              <div className="studio-flow-actions">
                <button type="button" disabled={reorderBusy || index === 0} onClick={() => moveStudioBlock(index, -1)} aria-label="Move block up">↑ Up</button>
                <button type="button" disabled={reorderBusy || index === studioBlocks.length - 1} onClick={() => moveStudioBlock(index, 1)} aria-label="Move block down">↓ Down</button>
              </div>
            </article>)}</div>
            </>}
        </aside>
      </div>
    </section>
    </>}

    {activeTab === 'teaching' && <>
    {!pairList.length && !message && <div className="panel">No pairs assigned yet.</div>}

    <section className="panel" style={{marginTop:'1rem'}}>
      <h2>Lesson access</h2>
      <p>Keep the existing teacher-controlled access model. Unlock the selected lesson for a whole pair or for one student.</p>
    </section>
    <div className="pair-grid">{pairList.map((pair) => <article className="pair-card" key={pair.id}>
      <p className="eyebrow">PAIR</p><h2>{pair.name}</h2>
      <p>Student 1: {pair.student_one_id}</p><p>Student 2: {pair.student_two_id || 'Not assigned'}</p>
      <div className="teacher-actions">
        <button onClick={() => openLesson(pair)}>Unlock for pair</button>
        <button onClick={() => openLesson(pair, pair.student_one_id)}>Unlock Student 1</button>
        {pair.student_two_id && <button onClick={() => openLesson(pair, pair.student_two_id)}>Unlock Student 2</button>}
      </div>
    </article>)}</div>

    <section className="panel" style={{marginTop:'1rem'}}>
      <h2>Assign homework</h2>
      <p>Create a short task for the selected lesson. Target time: 10–15 minutes.</p>
      <div className="admin-form">
        <div className="homework-assignment-row">
          <label className="material-label">Pair
            <select value={homeworkPair} onChange={(e) => { setHomeworkPair(e.target.value); setHomeworkStudent(''); }}>
              <option value="">Select pair</option>
              {pairList.map((pair) => <option key={pair.id} value={pair.id}>{pair.name}</option>)}
            </select>
          </label>
          <label className="material-label">Recipient
            <select value={homeworkStudent} onChange={(e) => setHomeworkStudent(e.target.value)} disabled={!homeworkPair}>
              <option value="">Whole pair</option>
              {homeworkPair && (() => {
                const pair = pairList.find((item) => item.id === homeworkPair);
                return pair ? <>{<option value={pair.student_one_id}>Student 1</option>}{pair.student_two_id && <option value={pair.student_two_id}>Student 2</option>}</> : null;
              })()}
            </select>
          </label>
        </div>
        <input placeholder="Homework title" value={homeworkTitle} onChange={(e) => setHomeworkTitle(e.target.value)} />
        <textarea placeholder="Instructions" value={homeworkInstructions} onChange={(e) => setHomeworkInstructions(e.target.value)} rows={4} />
        <div className="homework-assignment-row">
          <select value={homeworkKind} onChange={(e) => setHomeworkKind(e.target.value)}>
            <option value="writing">Writing</option><option value="speaking">Speaking</option><option value="listening">Listening</option><option value="video">Video</option><option value="revision">Revision</option>
          </select>
          <label className="material-label">Due date
            <input type="datetime-local" value={homeworkDue} onChange={(e) => setHomeworkDue(e.target.value)} />
          </label>
        </div>
        <button disabled={!selectedLesson || !homeworkPair || !homeworkInstructions.trim()} onClick={async () => {
          try {
            await createHomework({ lesson_id:selectedLesson, pair_id:homeworkPair, student_id:homeworkStudent || null, title:homeworkTitle.trim() || '10-minute practice', instructions:homeworkInstructions.trim(), kind:homeworkKind, due_at:homeworkDue ? new Date(homeworkDue).toISOString() : null });
            setMessage(homeworkStudent ? 'Homework assigned to the selected student.' : 'Homework assigned to the whole pair.');
            setHomeworkInstructions(''); setHomeworkTitle(''); setHomeworkDue(''); setHomeworkStudent('');
          } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not assign homework.'); }
        }}>Assign homework</button>
      </div>
    </section>

    <section className="panel teaching-overview">
      <div className="overview-head">
        <div><p className="eyebrow">LEARNING PULSE</p><h2>Progress at a glance</h2><p>See lesson activity, exercise completion and homework status without leaving the teaching room.</p></div>
        {overview && <label className="material-label">Student
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)}>
            {overview.students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
          </select>
        </label>}
      </div>
      {!overview ? <p>Select a pair to load progress.</p> : (() => {
        const studentProgress = overview.progress.filter((item) => item.student_id === selectedStudent);
        const completed = studentProgress.filter((item) => item.status === 'completed').length;
        const scored = studentProgress.filter((item) => item.score != null);
        const avg = scored.length ? Math.round(scored.reduce((sum, item) => sum + Number(item.score), 0) / scored.length) : 0;
        const seconds = studentProgress.reduce((sum, item) => sum + Number(item.seconds_spent || 0), 0);
        const pending = overview.homework.filter((item) => (item.student_id == null || item.student_id === selectedStudent) && item.submission_id == null).length;
        return <div className="overview-body">
          <div className="overview-metrics"><article><b>{completed}</b><span>completed blocks</span></article><article><b>{avg}%</b><span>average scored work</span></article><article><b>{Math.round(seconds / 60)}m</b><span>learning time</span></article><article><b>{pending}</b><span>homework pending</span></article></div>
          <div className="progress-table">{overview.lessons.map((lesson) => {
            const rows = studentProgress.filter((item) => item.lesson_id === lesson.id);
            const lessonCompleted = rows.some((item) => item.status === 'completed');
            const lessonScores = rows.filter((item) => item.score != null);
            const score = lessonScores.length ? Math.round(lessonScores.reduce((sum, item) => sum + Number(item.score), 0) / lessonScores.length) : null;
            return <div className="progress-row" key={lesson.id}><div><small>UNIT {lesson.unit_number} · LESSON {lesson.lesson_number}</small><strong>{lesson.title}</strong></div><span className={lessonCompleted ? 'progress-done' : rows.length ? 'progress-active' : 'progress-none'}>{lessonCompleted ? 'Completed' : rows.length ? 'In progress' : 'Not started'}</span><b>{score == null ? '—' : score + '%'}</b></div>;
          })}</div>
          <div className="homework-pulse"><div className="overview-section-title"><span className="eyebrow">HOMEWORK PULSE</span><strong>Recent tasks for {overview.students.find((student) => student.id === selectedStudent)?.name || 'student'}</strong></div>
            {!overview.homework.filter((item) => item.student_id == null || item.student_id === selectedStudent).length ? <p>No homework assigned to this student yet.</p> :
              overview.homework.filter((item) => item.student_id == null || item.student_id === selectedStudent).map((item) => <div className="homework-pulse-row" key={item.id}><div><b>{item.title}</b><small>{item.kind.toUpperCase()}{item.due_at ? ' · due ' + new Date(item.due_at).toLocaleDateString() : ''}</small></div><span className={item.submission_status === 'reviewed' ? 'progress-done' : item.submission_id ? 'progress-active' : 'progress-none'}>{item.submission_status === 'reviewed' ? 'Reviewed' : item.submission_id ? 'Submitted' : 'Pending'}</span></div>)}
          </div>
        </div>;
      })()}
    </section>

    <section className="panel" style={{marginTop:'1rem'}}>
      <h2>Submission inbox</h2>
      {!homeworkPair ? <p>Select a pair above to review submissions.</p> : !submissionList.length ? <p>No submissions from this pair yet.</p> :
        <div className="submission-list">{submissionList.map((item) => <article className="submission-card" key={item.id}>
          <div><span className="eyebrow">{item.kind.toUpperCase()} · {item.status}</span><h3>{item.title}</h3><p>Student: {item.student_id}</p><pre>{item.answer_json ? (() => { try { return JSON.stringify(JSON.parse(item.answer_json), null, 2); } catch { return item.answer_json; } })() : 'No text answer.'}</pre></div>
          <textarea rows={3} placeholder="Teacher feedback" value={feedback[item.id] ?? item.feedback ?? ''} onChange={(e) => setFeedback({...feedback,[item.id]:e.target.value})} />
          <button disabled={!feedback[item.id]?.trim()} onClick={async () => { try { await reviewSubmission(item.id, feedback[item.id]); setSubmissionList(await submissions(homeworkPair)); setMessage('Feedback saved.'); } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not review submission.'); } }}>Save feedback</button>
        </article>)}</div>}
    </section>
    </>}
  </section>;
}
